import { styled as muiStyled, css } from '@mui/material/styles';
import React from 'react';
import { useParams } from 'react-router-dom';

import Arranger from '../base/arranger';
import Text from '../base/text';

import { useAssessmentProgressOptional } from '~/client/components/assessment/assessment-progress-context';
import { useAssessmentFromId } from '~/client/hooks/assessment';
import { useDesignTokens } from '~/client/hooks/use-design-tokens';
import * as GQL from '~/graphql/generated/client';

interface TotalBundleStatsProps {
  controlBundleList: GQL.ControlBundleFragment[];
  className?: string;
  isActive?: boolean;
}

const TotalBundleStatsImpl: React.FC<TotalBundleStatsProps> = ({
  controlBundleList,
  className,
}) => {
  const t = useDesignTokens();
  const { projectId, candidateId, assessmentId, functionalModelId, agentId } =
    useParams();
  const effectiveProjectId =
    candidateId ?? agentId ?? functionalModelId ?? projectId;
  const progressContext = useAssessmentProgressOptional();
  const { assessment } = useAssessmentFromId(effectiveProjectId, assessmentId);

  const progressContextRef = React.useRef(progressContext);
  React.useEffect(() => {
    progressContextRef.current = progressContext;
  }, [progressContext]);

  const answerBundleList = assessment?.answerBundles ?? [];

  const { totalControlCount, totalAnswerCount } = React.useMemo(() => {
    let controls = 0;
    let answers = 0;

    controlBundleList.forEach((bundle) => {
      const answerBundle = answerBundleList.find(
        (ab) => ab.controlBundleId === bundle._id
      );

      const liveProgress = answerBundle?._id
        ? progressContext?.getBundleProgress(answerBundle._id)
        : undefined;

      controls +=
        liveProgress?.numControls ??
        answerBundle?.numControls ??
        bundle.controls?.length ??
        0;

      answers += liveProgress?.numAnswers ?? answerBundle?.numAnswers ?? 0;
    });

    return { totalControlCount: controls, totalAnswerCount: answers };
  }, [controlBundleList, answerBundleList, progressContext]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (
      !assessmentId ||
      totalControlCount === 0 ||
      !progressContextRef.current
    ) {
      return;
    }
    progressContextRef.current.updateAssessmentCompletion(
      assessmentId,
      (totalAnswerCount / totalControlCount) * 100
    );
  }, [totalAnswerCount, totalControlCount, assessmentId]);

  return (
    <Arranger
      className={`${className}`}
      alignContent="center"
      justify="space-between"
      sx={{ padding: 5, flexWrap: 'wrap' }}
    >
      <Text sx={{ color: t.nav.itemTextOnDark }}>Total</Text>
      <Text sx={{ color: t.nav.itemTextOnDark }}>
        {totalAnswerCount}/{totalControlCount}
      </Text>
    </Arranger>
  );
};

const TotalBundleStats = muiStyled(TotalBundleStatsImpl)(
  ({ theme }) => css`
    .bundle-name {
      margin-right: 4px;
      overflow: hidden;
      flex-grow: 1;
      color: ${theme.palette.text.secondary};
    }
    .nav-badge {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      padding: 0px 12px;
      width: 44px;
      height: 20px;
      background: ${theme.palette.action.hover};
      border-radius: ${theme.shape.borderRadius}px;
      flex: none;
      order: 1;
      flex-grow: 0;
      color: ${theme.palette.text.secondary};
    }

    &.active {
      .bundle-name {
        color: ${theme.palette.primary.main};
      }

      .nav-badge {
        background: ${theme.palette.primary.main};
        color: ${theme.palette.primary.contrastText};
      }
    }
  `
);

export default TotalBundleStats;
